<!--
	Plate — a framed image on paper, for real material that doesn't warrant the full
	AppWindow chrome: the shop photos, the location crops, a bare cut contour.

	Replaces the old DitherImage. The dither treatment it was named for had already
	been removed there, and rightly: dithering hid the product's quality, which is
	the whole trust signal. This shows the material crisp, in a frame that says
	"specimen on a sheet" rather than "hero image".

	`tone='dark'` keeps the product renders on their native graphite so the webp has
	no visible seam; `tone='light'` is for photographs.
	`ratio` + `focus` crop to a composition; omit `ratio` for the natural aspect.
-->
<script lang="ts">
	let {
		name,
		alt,
		width,
		height,
		caption,
		/** Right-hand side of the caption bar — a dimension, a part number. */
		note,
		tone = 'dark',
		ratio,
		focus = 'center',
		/** The LCP image must not be lazy. */
		priority = false,
		class: klass = ''
	}: {
		name: string;
		alt: string;
		width: number;
		height: number;
		caption?: string;
		note?: string;
		tone?: 'dark' | 'light';
		ratio?: string;
		focus?: string;
		priority?: boolean;
		class?: string;
	} = $props();
</script>

<figure class="plate {klass}" data-tone={tone}>
	<div class="plate__vp" class:plate__vp--fit={ratio} style:--ar={ratio} style:--focus={focus}>
		<img
			class="plate__img"
			src="/img/{name}.webp"
			{alt}
			{width}
			{height}
			loading={priority ? 'eager' : 'lazy'}
			fetchpriority={priority ? 'high' : undefined}
			decoding={priority ? 'sync' : 'async'}
		/>
	</div>
	{#if caption}
		<figcaption class="plate__cap">
			<span>{caption}</span>
			{#if note}<span class="plate__note">{note}</span>{/if}
		</figcaption>
	{/if}
</figure>

<style>
	.plate {
		margin: 0;
		overflow: hidden;
		border-radius: var(--radius-md);
		border: 1px solid var(--color-line);
		background-color: var(--color-surface);
		box-shadow: var(--shadow-card);
	}
	.plate[data-tone='dark'] {
		background-color: var(--color-app-950);
		border-color: var(--color-app-700);
	}

	.plate__vp {
		overflow: hidden;
	}
	.plate__vp--fit {
		aspect-ratio: var(--ar);
	}
	.plate__img {
		display: block;
		width: 100%;
		height: auto;
	}
	.plate__vp--fit .plate__img {
		height: 100%;
		object-fit: cover;
		object-position: var(--focus, center);
	}

	.plate__cap {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
		padding: 0.625rem 0.875rem;
		border-top: 1px solid var(--color-line);
		font-family: var(--font-mono);
		font-size: var(--text-mono-xs);
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--color-ink-400);
	}
	.plate[data-tone='dark'] .plate__cap {
		border-color: var(--color-app-700);
		color: var(--color-bone-500);
	}
	.plate__note {
		color: var(--color-cut-700);
		flex: none;
	}
	.plate[data-tone='dark'] .plate__note {
		color: var(--color-cut-400);
	}
</style>
